// Diffs the raw and rollup paths in the browser and reports any field that
// disagrees.
//
// The verification harnesses call the builders directly, so they prove the
// arithmetic on combinations chosen in advance. They cannot see a HOOK passing
// the wrong granularity, dropping a filter, or keying a cache badly -- the maths
// would be right and the answer still wrong. This runs on whatever is actually
// clicked, which is the only way that class of bug surfaces.
//
// Silent when the two agree. Loud, once per distinct mismatch, when they do not.

const seen = new Set<string>();

// Floating-point sums of the same numbers in a different order differ in the last
// bits, so this compares with a tolerance rather than at a fixed number of places.
//
// Rounding to 2dp looked tighter and was in fact worse: it turns a difference far
// below a cent into a reported one whenever a value lands on a .xx5 boundary and
// the two sides round opposite ways. That is exactly what revenue did -- Postgres
// numeric sums the pre-aggregated weeks exactly while the raw path accumulates
// doubles row by row (485061.21 against 485061.20999999996 on one doctor-year),
// and twelve of 1,200 drill rows crossed a boundary.
//
// 1e-7 relative is ~7 cents on a $677k figure and still catches anything that
// means something: a real disagreement here is a missing filter or a wrong
// grouping, which moves numbers by percent, not by parts per ten million.
const EPS_REL = 1e-7;
const EPS_ABS = 1e-6;

function near(a: number, b: number): boolean {
  return Math.abs(a - b) <= Math.max(EPS_ABS, Math.abs(a) * EPS_REL);
}

const round2 = (n: number) => Math.round(n * 100) / 100;

function isNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

export interface Mismatch {
  path: string;
  raw: unknown;
  rollup: unknown;
}

// Walks two values and collects every numeric or primitive disagreement.
function diff(a: unknown, b: unknown, path: string, out: Mismatch[], depth = 0): void {
  if (depth > 6 || out.length > 40) return;

  if (isNum(a) && isNum(b)) {
    if (!near(a, b)) out.push({ path, raw: round2(a), rollup: round2(b) });
    return;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      out.push({ path: `${path}.length`, raw: a.length, rollup: b.length });
    }
    for (let i = 0; i < Math.min(a.length, b.length); i++) {
      diff(a[i], b[i], `${path}[${i}]`, out, depth + 1);
    }
    return;
  }
  if (a && b && typeof a === "object" && typeof b === "object") {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
      diff(
        (a as Record<string, unknown>)[k],
        (b as Record<string, unknown>)[k],
        path ? `${path}.${k}` : k,
        out,
        depth + 1
      );
    }
    return;
  }
  if (a !== b) out.push({ path, raw: a, rollup: b });
}

/**
 * Compares one labelled pair. Logs at most once per distinct label+mismatch, so
 * a broken combination reports on first sight rather than on every render.
 */
export function comparePaths(label: string, raw: unknown, rollup: unknown): Mismatch[] {
  const out: Mismatch[] = [];
  diff(raw, rollup, "", out);

  if (out.length === 0) {
    const key = `ok:${label}`;
    if (!seen.has(key)) {
      seen.add(key);
      console.info(`[rollup-check] ${label} — match`);
    }
    return out;
  }

  stash(label, out);
  const key = `bad:${label}:${out.map((m) => m.path).join(",")}`;
  if (!seen.has(key)) {
    seen.add(key);
    console.error(
      `[rollup-check] ${label} — ${out.length} MISMATCH${out.length === 1 ? "" : "ES"}`,
      out.slice(0, 10)
    );
  }
  return out;
}

export function resetCompareLog(): void {
  seen.clear();
}

// Console objects cannot be read back programmatically, which makes a reported
// mismatch hard to act on. Stashing the last few here means the exact field names
// and both values are reachable from the console as
// window.__rollupMismatches.
declare global {
  interface Window {
    __rollupMismatches?: Record<string, Mismatch[]>;
  }
}

export function stash(label: string, mismatches: Mismatch[]): void {
  if (typeof window === "undefined") return;
  window.__rollupMismatches ??= {};
  if (mismatches.length) window.__rollupMismatches[label] = mismatches;
  else delete window.__rollupMismatches[label];
}
